import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchBulkProcessEligibleGigSteps } from 'services/api/campaign/collaboration';
import { fetchCampaignMilestonesThunk } from 'slices/campaigns/milestones/thunk';
import { fetchCampaign } from 'slices/campaigns/thunk';
import { setHeaderCampaignData } from 'slices/ui/reducer';
import { AppDispatch, RootState } from 'store';
import Select from 'react-select'
import BulkProcessSheet from 'Components/campaigns/collaborations/BulkProcess/BulkProcessSheet';
import "./BulkProcess.scss"
import { GigType } from 'types/campaign/collaborations';
import { LoadingOverlay } from 'Components/Common/LoadingOverlay';

export interface EligibleGigStep { name: string, slug: string, gig_type: GigType };

const CollaborationsBulkProcess = () => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const { campaignId } = useParams();

    const campaignDetail = useSelector((state: RootState) => state.campaigns.items[campaignId!]);
    const campaignData = campaignDetail?.data ?? null;

    const campaignMilestones = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!])

    const milestones = campaignMilestones?.milestonesData?.milestones ?? [];


    const [selectedMilestone, setSelectedMilestone] = useState<number | null>(null);
    const [selectedGigStep, setSelectedGigStep] = useState<EligibleGigStep | null>(null);

    const [milestoneEligibleSteps, setMilestoneEligibleSteps] = useState<Record<string, EligibleGigStep[]>>({});

    const [overlay, setOverlay] = useState<{ visible: boolean, text?: string }>({ visible: false })


    useEffect(() => {
        if (campaignId && !campaignData) {
            dispatch(fetchCampaign({ campaignId }));
        }
    }, [campaignId, dispatch, campaignData]);

    useEffect(() => {
        if (campaignData) {
            dispatch(setHeaderCampaignData({ campaign: campaignData }));
        }

        return () => {
            if (campaignData) {
                dispatch(setHeaderCampaignData({ campaign: null, detailPage: false }));
            }
        }
    }, [campaignData])

    const fetchCampaignMilestones = async () => {
        if (campaignId) {
            try {
                setOverlay({ visible: true, text: "Fetching Milestones..." })
                await dispatch(fetchCampaignMilestonesThunk(campaignId!)).unwrap()
            } catch (error) {
                console.log(error);
            } finally {
                setOverlay({ visible: false });
            }
        }
    }

    useEffect(() => {
        fetchCampaignMilestones();
    }, [campaignId, dispatch]);

    useEffect(() => {
        if (!selectedMilestone && Array.isArray(milestones) && milestones.length > 0) {
            const defaultSelectedMilestoneId = [...milestones].sort((a, b) => a.sort_order - b.sort_order)[0].id;
            if (defaultSelectedMilestoneId) {
                setSelectedMilestone(defaultSelectedMilestoneId);
            }
        }
    }, [selectedMilestone, milestones])

    const selectedMilestoneObj = useMemo(() => {
        return milestones.find((m) => m.id === selectedMilestone);
    }, [selectedMilestone, milestones])

    const fetchEligibleGigSteps = async (milestoneId: number) => {
        try {
            setOverlay({ visible: true, text: 'Fetching eligible steps...' });
            if (!selectedMilestoneObj) return;
            const results = await fetchBulkProcessEligibleGigSteps(selectedMilestoneObj.gig_type.slug);
            setMilestoneEligibleSteps({ ...milestoneEligibleSteps, [milestoneId]: results ?? [] });
        } catch (error) {
            console.log(error);
        } finally {
            setOverlay({ visible: false })
        }
    }

    useEffect(() => {
        if (selectedMilestone) {
            if (!milestoneEligibleSteps[selectedMilestone]) {
                fetchEligibleGigSteps(selectedMilestone)
            }
        }
    }, [milestoneEligibleSteps, selectedMilestone])

    const getSelectedMilestoneOption = () => {
        const milestone = milestones.find((m) => m.id === selectedMilestone);
        if (milestone) {
            return { label: `#${milestone.sort_order} ${milestone.gig_type.name}`, value: milestone.id };
        }
        return null
    }

    const getSelectedGigStepOption = () => {
        if (!selectedMilestone) return null;
        const gigSteps = milestoneEligibleSteps[selectedMilestone] ?? [];
        const gigStep = gigSteps.find((gs) => gs.slug === selectedGigStep?.slug);
        if (gigStep) {
            return { label: gigStep.name, value: gigStep.slug }
        }
        return null;
    }

    return (
        <div>
            <LoadingOverlay text={overlay.text} visible={overlay.visible ?? false} />
            <div className="campaign-page">
                <div className="d-flex justify-content-between align items-center">
                    <h1 className="campaign-page-title m-0">Bulk Process</h1>
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <Select
                            classNamePrefix="select"
                            isSearchable={true}
                            name="milestone"
                            placeholder='Select Milestone'
                            value={getSelectedMilestoneOption()}
                            onChange={((selected) => {
                                setSelectedGigStep(null);
                                setSelectedMilestone(selected?.value ?? null)
                            })}
                            options={[...milestones].sort((a, b) => a.sort_order - b.sort_order).map((milestone) => ({ label: `#${milestone.sort_order} ${milestone.gig_type.name}`, value: milestone.id }))}
                        />
                        <Select
                            classNamePrefix="select"
                            isSearchable={true}
                            name="gig-step"
                            placeholder='Select Gig Step'
                            value={getSelectedGigStepOption()}
                            onChange={(selected) => {
                                const gigSteps = milestoneEligibleSteps[selectedMilestone ?? ''] ?? [];
                                const foundStep = selected?.value ? gigSteps.find((s) => s.slug === selected.value) : null;
                                setSelectedGigStep(foundStep ?? null);
                            }}
                            options={
                                (milestoneEligibleSteps[selectedMilestone ?? ''] ?? []).map((gigstep) => ({
                                    label: gigstep.name,
                                    value: gigstep.slug
                                }))
                            }
                        />
                    </div>
                </div>
                <div className='mt-3'>
                    {
                        selectedGigStep && selectedMilestone &&
                        <BulkProcessSheet gigStep={selectedGigStep} milestoneId={selectedMilestone} />
                    }
                    {
                        (!selectedGigStep || !selectedMilestone) &&
                        <div className="d-flex w-100 justify-content-center align-items-center">
                            <div className="length-zero">
                                <h3>Please select campaign milestone and a gig step to process in bulk!</h3>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default CollaborationsBulkProcess